import { useMemo, useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";

/** Folder rows, nested by `parentId`. `folders` arrives ordered by idPath, so children follow parents. */
const FolderTree = ({
  folders = [],
  selectedId = null,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  canCreate = false,
  canEdit = false,
  canDelete = false,
}) => {
  const [collapsed, setCollapsed] = useState({});
  const [menu, setMenu] = useState({ anchor: null, folder: null });

  const childrenByParent = useMemo(() => {
    const map = new Map();
    folders.forEach((folder) => {
      const key = folder.parentId ?? "root";
      map.set(key, [...(map.get(key) || []), folder]);
    });
    return map;
  }, [folders]);

  const renderLevel = (parentKey) =>
    (childrenByParent.get(parentKey) || []).map((folder) => {
      const children = childrenByParent.get(folder.id) || [];
      const isCollapsed = Boolean(collapsed[folder.id]);
      return (
        <Box key={folder.id} sx={{ pl: folder.depth ? 2 : 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              py: 0.5,
              cursor: "pointer",
              bgcolor: selectedId === folder.id ? "action.selected" : "transparent",
              borderRadius: 1,
            }}
            onClick={() => onSelect(folder.id)}
          >
            <IconButton
              size="small"
              disabled={children.length === 0}
              onClick={(event) => {
                event.stopPropagation();
                setCollapsed((current) => ({ ...current, [folder.id]: !current[folder.id] }));
              }}
            >
              {children.length === 0 ? null : isCollapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
            <FolderIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ flex: 1 }} noWrap title={folder.name}>
              {folder.name}
            </Typography>
            {(canCreate || canEdit || canDelete) && (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenu({ anchor: event.currentTarget, folder });
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {!isCollapsed && renderLevel(folder.id)}
        </Box>
      );
    });

  const closeMenu = () => setMenu({ anchor: null, folder: null });

  return (
    <Box>
      {folders.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No folders yet.
        </Typography>
      )}
      {renderLevel("root")}
      <Menu anchorEl={menu.anchor} open={Boolean(menu.anchor)} onClose={closeMenu}>
        {canCreate && (
          <MenuItem
            onClick={() => {
              onCreate(menu.folder.id);
              closeMenu();
            }}
          >
            New subfolder
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem
            onClick={() => {
              onRename(menu.folder);
              closeMenu();
            }}
          >
            Rename
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              onDelete(menu.folder);
              closeMenu();
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default FolderTree;
